import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, Renderer2, SecurityContext, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TooltipService } from './tooltip-service';

@Directive({
    selector: '[appTooltip]',
    standalone: false
})
export class TooltipDirective implements OnDestroy, OnChanges {
   @Input('appTooltip') tooltipText: string = '';
   @Input() useHtml: boolean = false;
   @Input('appTooltipDisabled') disabled: boolean = false;


   private tooltipElement: HTMLElement;
   private isTouchDevice: boolean = 'ontouchstart' in window;
   private tooltipVisible: boolean = false;

   constructor(
      private el: ElementRef,
      private renderer: Renderer2,
      private tooltipService: TooltipService,
      private sanitizer: DomSanitizer
   ) {}

   ngOnChanges(changes: SimpleChanges) {
      if (changes['disabled'] && this.disabled) {
         this.hideTooltip();
      }
   }

   ngOnDestroy() {
      if (this.tooltipElement) {
         this.tooltipService.destroyAllTooltips();
         this.renderer.removeChild(document.body, this.tooltipElement);
         this.tooltipElement = null!;
      }
   }

   @HostListener('mouseenter') onMouseEnter() {
      if (this.isTouchDevice || this.disabled) return;
      this.createTooltip();
      this.showTooltip();
   }

   @HostListener('mouseleave') onMouseLeave() {
      if (this.isTouchDevice) return;
      this.hideTooltip();
   }

   @HostListener('mousemove', ['$event']) onMouseMove(event: MouseEvent) {
      if (this.tooltipElement && !this.isTouchDevice) {
         const tooltipHeight = this.tooltipElement.offsetHeight;
         const tooltipWidth = this.tooltipElement.offsetWidth;

         const table = this.el.nativeElement.closest('table');
         const tableRect = table ? table.getBoundingClientRect() : null;
         let left = event.clientX + 10;
         let top = event.clientY + 10;

         if (tableRect) {
            const tableBottom = tableRect.bottom;
            if (top + tooltipHeight > tableBottom) {
               top = event.clientY - tooltipHeight - 10;
            }
         }

         if (top < 0) {
            top = 10;
         }

         if (left + tooltipWidth > window.innerWidth) {
            left = window.innerWidth - tooltipWidth - 10;
         }

         this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
         this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
      }
      if (this.tooltipText === 'hideTooltip') {
         this.hideTooltip();
      } else {
         this.showTooltip();
      }
   }

   @HostListener('click') onClick() {
      if (!this.isTouchDevice || this.disabled) return;
      if (this.tooltipVisible) {
         this.hideTooltip();
      } else {
         this.createTooltip();
         this.showTooltip();
      }
   }

   private createTooltip() {
      if (!this.tooltipText?.trim()) return;
      if (!this.tooltipElement) {
         this.tooltipElement = this.renderer.createElement('span');
         this.renderer.addClass(this.tooltipElement, 'tooltiptext');
         const tooltipContainer = this.el.nativeElement.closest('.cdk-overlay-pane') ?? document.body;
         this.renderer.appendChild(tooltipContainer, this.tooltipElement);
         this.tooltipService.registerTooltip(this.tooltipElement);
      }
   }

   private showTooltip() {
      if (this.disabled || !this.tooltipText?.trim()) {
         return;
      }
      if (this.tooltipElement) {
         if (this.useHtml) {
            const safeHtml = this.sanitizeHtml(this.tooltipText);
            this.renderer.setProperty(this.tooltipElement, 'innerHTML', safeHtml ?? '');
         } else {
            this.renderer.setProperty(this.tooltipElement, 'innerText', this.tooltipText);
         }
         this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
         this.renderer.setStyle(this.tooltipElement, 'opacity', '1');
         this.tooltipVisible = true;
      }
   }

   private hideTooltip() {
      if (this.tooltipElement) {
         this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
         this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
         this.tooltipVisible = false;
      }
   }

   private sanitizeHtml(html: string): string | null {
      return this.sanitizer.sanitize(SecurityContext.HTML, html);
   }
}
